// PROJECT 11* — gedeelde design-tokens, Particles en helpers.
import { useEffect, useRef } from "react";

export const P11 = {
  bg: "#080808",
  bg2: "#171717",
  yellow: "#FFE44D",
  text: "#F5F5F5",
  gray: "#858585",
  gray2: "#999999",
  border: "#292929",
};

export const P11_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/825446ec66ea170e_5D4FB4B0-9943-4088-95CE-49FE341A9E23.webp";
export const JOH_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/34ee825b4f390569_06DA39B9-ADCA-4588-B728-75586D5B67E6.png";
// Donker logo op transparante achtergrond — op zwart inverteren naar wit
export const JOH_LOGO_STYLE = { height: 66, filter: "invert(1)", opacity: 0.85 };

export const TERMS_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/oz5ux43z_JOH_Boxing_Algemene_voorwaarden.pdf";
export const PRIVACY_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/o4us3pcd_JOH_Boxing_Privacyverklaring.pdf";

// Door de gebruiker aangeleverde homepage-achtergrondvideo (alleen Project 11)
export const P11_BG_VIDEO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/d00b47daf9f1e05a_p11%20stage%201.mp4";

// Fullscreen achtergrondvideo + donkere lees-overlay. Laag z-0/z-1: Particles en content erboven.
// Autoplay/muted/loop; reduced-motion → stilstaand kader. WebM-fallback voor browsers zonder H.264.
export function BgVideo() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) ref.current?.pause();
  }, []);
  return (
    <>
      <video ref={ref} className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0 }}
        autoPlay muted loop playsInline preload="auto" aria-hidden="true" tabIndex={-1} data-testid="p11-bg-video">
        <source src={P11_BG_VIDEO} type="video/mp4" />
        <source src={`${process.env.REACT_APP_BACKEND_URL}/api/project11/public/bg-video.webm`} type="video/webm" />
      </video>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1, background: "rgba(8,8,8,0.68)" }} aria-hidden="true" data-testid="p11-bg-overlay" />
    </>
  );
}

export const fmtDate = (iso, lang = "nl") => {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
};

// Subtiele zwevende deeltjes (canvas), respecteert reduced-motion.
export function Particles({ density = 70 }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    const dots = [];
    const resize = () => {
      w = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);
    for (let i = 0; i < density; i++) {
      dots.push({
        x: Math.random(), y: Math.random(),
        r: Math.random() * 1.6 + 0.4,
        vx: (Math.random() - 0.5) * 0.00008,
        vy: (Math.random() - 0.5) * 0.00006,
        yellow: Math.random() < 0.18,
        o: Math.random() * 0.4 + 0.15,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const d of dots) {
        if (!reduced) {
          d.x += d.vx; d.y += d.vy;
          if (d.x < 0) d.x = 1; if (d.x > 1) d.x = 0;
          if (d.y < 0) d.y = 1; if (d.y > 1) d.y = 0;
        }
        ctx.beginPath();
        ctx.arc(d.x * w, d.y * h, d.r * window.devicePixelRatio, 0, Math.PI * 2);
        ctx.fillStyle = d.yellow ? `rgba(255,228,77,${d.o})` : `rgba(245,245,245,${d.o * 0.7})`;
        ctx.fill();
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [density]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" data-testid="p11-particles" />;
}

// Gedeelde P11 stijlklassen (CSS in index.css, prefix p11-)
export const p11cx = {
  h1: "p11-h1",
  h2: "p11-h2",
  mono: "p11-mono",
  label: "p11-label",
  btn: "p11-btn",
  btnGhost: "p11-btn-ghost",
  input: "p11-input",
  divider: "p11-divider",
};
